import { useEffect, useRef, useState } from 'react';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { useImmerReducer } from 'use-immer';
import { SmartContext } from '../../../library/Core/SmartContext';
import smartReducer from '../../../library/Core/SmartReducer';
import { DispatchEvent, State } from '../../../library/Core/SmartTypes';
import { axiosClient } from '../../../library/Service/axiosClient';
import Layout from './Layout';

const AddAttendance = () => {
    const loaderData = useLoaderData() as State;
    const { eventId } = useParams();
    const navigate = useNavigate();

    const [state, dispatch] = useImmerReducer<State, DispatchEvent>(smartReducer, {
        flags: { isDataLoading: false, showFormErrors: 0 },
        formValidationErrors: {},
        routeInfo: loaderData.routeInfo,
        data: loaderData.data,
        actions: {},
    } as any);

    const [totalRegistration, setTotalRegistration] = useState('');
    const [totalAttendee, setTotalAttendee] = useState('');
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvUrl, setCsvUrl] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!eventId) {
        navigate('/event/add');
    }

    // Load existing attendance data
    useEffect(() => {
        if (!eventId) return;
        axiosClient()
            .get(`${process.env.REACT_APP_BROADCAST_SERVER}/event-attendance/${eventId}`)
            .then((res) => {
                const d = res.data?.data;
                if (d) {
                    setTotalRegistration(d.total_registration != null ? String(d.total_registration) : '');
                    setTotalAttendee(d.total_attendee != null ? String(d.total_attendee) : '');
                    setCsvUrl(d.attendee_data_file || '');
                }
            })
            .catch(() => {});
    }, [eventId]);

    const validate = () => {
        const temp: Record<string, string> = {};
        if (!totalRegistration) {
            temp.totalRegistration = 'Total Registration is required';
        } else if (!/^\d+$/.test(totalRegistration)) {
            temp.totalRegistration = 'Enter a valid number';
        }
        if (!totalAttendee) {
            temp.totalAttendee = 'Attendee Count is required';
        } else if (!/^\d+$/.test(totalAttendee)) {
            temp.totalAttendee = 'Enter a valid number';
        }
        setErrors(temp);
        return Object.keys(temp).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        setSaveSuccess(false);

        const formData = new FormData();
        formData.append('eventId', eventId!);
        formData.append('totalRegistration', totalRegistration);
        formData.append('totalAttendee', totalAttendee);
        if (csvFile) formData.append('csv', csvFile);

        try {
            const res = await axiosClient().post(
                `${process.env.REACT_APP_BROADCAST_SERVER}/event-attendance/save`,
                formData
            );
            if (res.data?.csvUrl) setCsvUrl(res.data.csvUrl);
            setSaveSuccess(true);
        } catch (err: any) {
            alert(err?.message || 'Failed to save. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <Layout>
                <div className="col-12">
                    <div className="p-3 p-sm-4">
                        <h5 className="font-18 font-600 mb-4">Add Attendance</h5>
                        <div>
                            <div className="row g-4">

                                {/* Total Registration */}
                                <div className="col-md-6">
                                    <label className="form-label font-500 mb-1">
                                        Total Registration Count <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={`form-control${errors.totalRegistration ? ' is-invalid' : ''}`}
                                        placeholder="e.g. 150"
                                        value={totalRegistration}
                                        onChange={(e) => {
                                            setTotalRegistration(e.target.value.replace(/\D/g, ''));
                                            setErrors((p) => ({ ...p, totalRegistration: '' }));
                                            setSaveSuccess(false);
                                        }}
                                    />
                                    {errors.totalRegistration && (
                                        <div className="invalid-feedback">{errors.totalRegistration}</div>
                                    )}
                                </div>

                                {/* Attendee Count */}
                                <div className="col-md-6">
                                    <label className="form-label font-500 mb-1">
                                        Attendee Count <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={`form-control${errors.totalAttendee ? ' is-invalid' : ''}`}
                                        placeholder="e.g. 120"
                                        value={totalAttendee}
                                        onChange={(e) => {
                                            setTotalAttendee(e.target.value.replace(/\D/g, ''));
                                            setErrors((p) => ({ ...p, totalAttendee: '' }));
                                            setSaveSuccess(false);
                                        }}
                                    />
                                    {errors.totalAttendee && (
                                        <div className="invalid-feedback">{errors.totalAttendee}</div>
                                    )}
                                </div>

                                {/* CSV Upload */}
                                <div className="col-12">
                                    <label className="form-label font-500 mb-1">
                                        Attendee Data File <span className="text-muted font-400">(CSV, max 10 MB)</span>
                                    </label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        className="form-control"
                                        onChange={(e) => {
                                            setCsvFile(e.target.files?.[0] || null);
                                            setSaveSuccess(false);
                                        }}
                                    />
                                </div>

                                {/* Download link — shown when a file was previously uploaded */}
                                {csvUrl && (
                                    <div className="col-12">
                                        <div className="alert alert-info p-2 mb-0 d-flex align-items-center gap-2">
                                            <i className="bi bi-file-earmark-spreadsheet-fill fs-5"></i>
                                            <span>Attendee file uploaded:</span>
                                            <a href={csvUrl} target="_blank" rel="noreferrer" download className="fw-500">
                                                Download CSV
                                            </a>
                                        </div>
                                    </div>
                                )}

                                {/* Success message */}
                                {saveSuccess && (
                                    <div className="col-12">
                                        <div className="alert alert-success p-2 mb-0">
                                            Attendance saved successfully!
                                        </div>
                                    </div>
                                )}

                                {/* Submit */}
                                <div className="col-12">
                                    <button
                                        type="button"
                                        className="btn btn-green d-flex align-items-center gap-2"
                                        style={{ minWidth: 120 }}
                                        disabled={isSubmitting}
                                        onClick={handleSubmit}
                                    >
                                        {isSubmitting && (
                                            <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                        )}
                                        <span>{isSubmitting ? 'Saving…' : 'Save'}</span>
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </SmartContext.Provider>
    );
};

export default AddAttendance;
